import { collection, deleteDoc, doc, getDoc, getDocs, onSnapshot, updateDoc } from 'firebase/firestore';
import { createDocument, isDocIdTaken } from '@/data/util_documentIds';
import { db } from '@/firebaseInit';
import { coll_suppliers, compareSuppliers, normaliseSupplier, type SupplierType } from '@/types/types_supplier';

// Data-access layer for suppliers, backed by Firestore. Mirrors the shape of
// guidesRepo.ts so the autosave/history stack works the same way.

const suppliersCollection = collection(db, coll_suppliers);

function toSupplier(id: string, data: Record<string, unknown>): SupplierType {
  return normaliseSupplier(data as Partial<SupplierType>, id);
}

export async function listSuppliers(): Promise<SupplierType[]> {
  const snap = await getDocs(suppliersCollection);
  return snap.docs.map((d) => toSupplier(d.id, d.data())).sort(compareSuppliers);
}

export async function getSupplier(id: string): Promise<SupplierType | undefined> {
  const snap = await getDoc(doc(suppliersCollection, id));
  return snap.exists() ? toSupplier(snap.id, snap.data()) : undefined;
}

/**
 * Subscribes to every supplier in real time. Mirrors Firestore's onSnapshot: the
 * initial value and every change after. Returns an unsubscribe function.
 *
 * Sorted here rather than with an `orderBy`, as the guide roster is: it is a
 * locale-aware name comparison over a few dozen documents, which the client is
 * the right place for.
 */
export function subscribeSuppliers(
  onChange: (suppliers: SupplierType[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    suppliersCollection,
    (snap) => onChange(snap.docs.map((d) => toSupplier(d.id, d.data())).sort(compareSuppliers)),
    onError,
  );
}

/** Subscribes to one supplier by id, so an edit to its absences reaches an open experience page. */
export function subscribeSupplier(
  id: string,
  onChange: (supplier: SupplierType | undefined) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    doc(suppliersCollection, id),
    (snap) => onChange(snap.exists() ? toSupplier(snap.id, snap.data()) : undefined),
    onError,
  );
}

/**
 * Whether a supplier already exists under this id. For a form to show while
 * somebody types; `createSupplier` is what keeps the id unique.
 */
export async function isSupplierIdTaken(id: string): Promise<boolean> {
  return isDocIdTaken(suppliersCollection, id);
}

/**
 * Creates a supplier, refusing an id another supplier already holds.
 *
 * Editing goes through `updateSupplierFields` (the form autosaves per field), so
 * nothing here needs to double as an update.
 */
export async function createSupplier(supplier: SupplierType): Promise<SupplierType> {
  return createDocument(suppliersCollection, supplier);
}

/**
 * Field-level update of a single supplier, used by autosave. `updateObj` may
 * contain dotted field paths (`closurePeriods.{id}.reason`, `history.*`).
 * `updatedAt` is stamped here.
 */
export async function updateSupplierFields(id: string, updateObj: Record<string, unknown>): Promise<void> {
  await updateDoc(doc(suppliersCollection, id), {
    ...updateObj,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Deletes a supplier.
 *
 * Nothing here unlinks the experiences pointing at it — the list page refuses to
 * offer the delete while any do, because rewriting somebody else's documents as
 * a side effect of this is worse than the dangling id. A reader that cannot find
 * the supplier simply inherits no absences.
 */
export async function deleteSupplier(id: string): Promise<void> {
  await deleteDoc(doc(suppliersCollection, id));
}
